import React, { useState, useRef, useEffect } from 'react';
import { FiPaperclip, FiSend, FiSmile } from 'react-icons/fi';
import { FileIcon, defaultStyles } from 'react-file-icon';
import ApiService from '../services/ApiService';
import '../styles/ChatWindow.css';

const ALLOWED_TAGS = new Set(['A', 'B', 'STRONG', 'I', 'EM', 'DEL', 'S', 'INS', 'U', 'PRE', 'CODE', 'BLOCKQUOTE', 'BR']);
const MAX_UPLOAD_SIZE_BYTES = 100 * 1024 * 1024;
const EMOJI_CATEGORIES = {
  smileys: ['\u{1F600}', '\u{1F604}', '\u{1F601}', '\u{1F602}', '\u{1F642}', '\u{1F609}', '\u{1F60A}', '\u{1F60D}', '\u{1F618}', '\u{1F60E}', '\u{1F914}', '\u{1F610}', '\u{1F634}', '\u{1F622}', '\u{1F62D}', '\u{1F621}', '\u{1F973}', '\u{1F929}'],
  gestures: ['\u{1F44D}', '\u{1F44E}', '\u{1F44F}', '\u{1F64F}', '\u{1F44B}', '\u{1F44C}', '\u{270C}\u{FE0F}', '\u{1F91D}', '\u{1F4AA}', '\u{1F64C}', '\u{1F91E}', '\u{1FAF6}'],
  animals: ['\u{1F436}', '\u{1F431}', '\u{1F42D}', '\u{1F439}', '\u{1F430}', '\u{1F98A}', '\u{1F43B}', '\u{1F43C}', '\u{1F42F}', '\u{1F981}', '\u{1F42E}', '\u{1F438}', '\u{1F435}', '\u{1F427}'],
  food: ['\u{1F34E}', '\u{1F34A}', '\u{1F34B}', '\u{1F34C}', '\u{1F353}', '\u{1F352}', '\u{1F349}', '\u{1F95D}', '\u{1F355}', '\u{1F354}', '\u{1F35F}', '\u{1F32E}', '\u{1F363}', '\u{1F370}', '\u{2615}', '\u{1F375}'],
  symbols: ['\u{2764}\u{FE0F}', '\u{1F499}', '\u{1F49A}', '\u{1F49B}', '\u{1F5A4}', '\u{1F525}', '\u{2728}', '\u{1F389}', '\u{2705}', '\u{274C}', '\u{1F4AC}', '\u{1F4CE}', '\u{1F4DE}', '\u{1F680}']
};
const EMOJI_CATEGORY_TABS = [
  { key: 'recent', label: '\u{1F558}' },
  { key: 'smileys', label: '\u{1F60A}' },
  { key: 'gestures', label: '\u{1F44D}' },
  { key: 'animals', label: '\u{1F436}' },
  { key: 'food', label: '\u{1F355}' },
  { key: 'symbols', label: '\u{2764}\u{FE0F}' }
];
const RECENT_EMOJI_STORAGE_KEY = 'emoji_recent';
const MAX_RECENT_EMOJIS = 24;
const DELETE_MESSAGE_WINDOW_MS = 3 * 60 * 60 * 1000;

function ChatWindow({
  chat,
  messages,
  hasMoreMessages = false,
  loadingOlderMessages = false,
  onLoadOlderMessages,
  currentUser,
  onSendMessage,
  onEditMessage,
  onDeleteMessage,
  onInviteToDialog,
  onKickFromDialog,
  onLeaveDialog,
  error,
  lastReadAt = 0
}) {
  const MIN_COMPOSER_HEIGHT = 46;
  const MAX_COMPOSER_HEIGHT = 260;
  const [composerHtml, setComposerHtml] = useState('');
  const [composerText, setComposerText] = useState('');
  const [composerHeight, setComposerHeight] = useState(MIN_COMPOSER_HEIGHT);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [requestContactLoading, setRequestContactLoading] = useState(false);
  const [requestContactConfirmOpen, setRequestContactConfirmOpen] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiCategory, setEmojiCategory] = useState('recent');
  const [emojiSearch, setEmojiSearch] = useState('');
  const [recentEmojis, setRecentEmojis] = useState(() => loadRecentEmojis());
  const [imageViewer, setImageViewer] = useState({ open: false, src: '', alt: '' });
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, message: null, isIncoming: false });
  const [composerContextMenu, setComposerContextMenu] = useState({ visible: false, x: 0, y: 0 });
  const [highlightedMessageId, setHighlightedMessageId] = useState('');
  const [dialogParticipants, setDialogParticipants] = useState([]);
  const [participantMenu, setParticipantMenu] = useState({ visible: false, x: 0, y: 0, participant: null });
  const [formatState, setFormatState] = useState({
    bold: false,
    italic: false,
    underline: false,
    strike: false,
  });
  const messagesEndRef = useRef(null);
  const messagesListRef = useRef(null);
  const composerRef = useRef(null);
  const fileInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const emojiToggleRef = useRef(null);
  const messageNodeMapRef = useRef(new Map());
  const highlightTimeoutRef = useRef(null);
  const composerResizeRef = useRef({ active: false, startY: 0, startHeight: MIN_COMPOSER_HEIGHT, cleanup: null });
  const draftByChatRef = useRef(new Map());
  const currentChatIdRef = useRef('');
  const suppressAutoScrollRef = useRef(false);

  useEffect(() => {
    if (suppressAutoScrollRef.current) {
      suppressAutoScrollRef.current = false;
      return undefined;
    }

    const scrollToBottom = () => {
      const list = messagesListRef.current;
      if (list) {
        list.scrollTop = list.scrollHeight;
        return;
      }
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    };

    scrollToBottom();
    const t1 = setTimeout(scrollToBottom, 80);
    const t2 = setTimeout(scrollToBottom, 220);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [chat?.chatId, messages.length]);

  useEffect(() => {
    if (loadingOlderMessages) {
      suppressAutoScrollRef.current = true;
    }
  }, [loadingOlderMessages]);

  useEffect(() => {
    const nextChatId = chat?.chatId ? String(chat.chatId) : '';
    currentChatIdRef.current = nextChatId;

    const draft = nextChatId ? draftByChatRef.current.get(nextChatId) : null;
    const nextDraftHtml = String(draft?.html || '');
    const nextDraftText = String(draft?.text || '');
    const nextDraftHeightRaw = Number(draft?.height || MIN_COMPOSER_HEIGHT);
    const nextDraftHeight = Math.min(MAX_COMPOSER_HEIGHT, Math.max(MIN_COMPOSER_HEIGHT, nextDraftHeightRaw));

    if (composerRef.current) {
      composerRef.current.innerHTML = nextDraftHtml;
    }
    setComposerHtml(nextDraftHtml);
    setComposerText(nextDraftText);
    setAttachedFile(null);
    setEditingMessageId(null);
    setReplyTo(null);
    setUploadLoading(false);
    setShowEmojiPicker(false);
    setEmojiSearch('');
    setEmojiCategory('recent');
    setImageViewer({ open: false, src: '', alt: '' });
    setDialogParticipants([]);
    setFormatState({ bold: false, italic: false, underline: false, strike: false, });
    setContextMenu({ visible: false, x: 0, y: 0, message: null, isIncoming: false });
    setParticipantMenu({ visible: false, x: 0, y: 0, participant: null });
    setComposerContextMenu({ visible: false, x: 0, y: 0 });
    setComposerHeight(nextDraftHeight);
  }, [chat?.chatId]);

  useEffect(() => {
    const chatId = currentChatIdRef.current;
    if (!chatId) return;

    const normalizedHtml = normalizeComposerHtml(composerHtml);
    const normalizedText = String(composerText || '').trim();
    const hasDraft = Boolean(normalizedHtml || normalizedText);

    if (!hasDraft) {
      draftByChatRef.current.delete(chatId);
      return;
    }

    draftByChatRef.current.set(chatId, {
      html: composerHtml || '',
      text: composerText || '',
      height: composerHeight
    });
  }, [composerHeight, composerHtml, composerText]);

  useEffect(() => {
    if (!chat?.chatId) {
      setDialogParticipants([]);
      return undefined;
    }

    let disposed = false;
    let intervalId = null;

    const loadParticipants = async () => {
      try {
        const items = await ApiService.getDialogParticipants(chat.chatId);
        if (!disposed) {
          setDialogParticipants(Array.isArray(items) ? items : []);
        }
      } catch {
        if (!disposed) {
          setDialogParticipants([]);
        }
      }
    };

    loadParticipants();
    intervalId = window.setInterval(loadParticipants, 15000);

    return () => {
      disposed = true;
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [chat?.chatId]);

  useEffect(() => () => {
    if (typeof composerResizeRef.current.cleanup === 'function') {
      composerResizeRef.current.cleanup();
    }
    document.body.classList.remove('is-resizing-composer');
  }, []);

  const handleComposerResizeStart = (e) => {
    if (typeof e.button === 'number' && e.button !== 0) return;
    const startY = e.clientY;
    const startHeight = composerHeight;
    composerResizeRef.current.active = true;
    composerResizeRef.current.startY = startY;
    composerResizeRef.current.startHeight = startHeight;
    document.body.classList.add('is-resizing-composer');

    const onMouseMove = (moveEvent) => {
      const delta = startY - moveEvent.clientY;
      const nextHeight = Math.max(
        MIN_COMPOSER_HEIGHT,
        Math.min(MAX_COMPOSER_HEIGHT, startHeight + delta)
      );
      setComposerHeight(nextHeight);
      moveEvent.preventDefault();
    };

    const stopResize = () => {
      composerResizeRef.current.active = false;
      document.body.classList.remove('is-resizing-composer');
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', stopResize);
      window.removeEventListener('blur', stopResize);
      composerResizeRef.current.cleanup = null;
    };

    composerResizeRef.current.cleanup = stopResize;
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', stopResize);
    window.addEventListener('blur', stopResize);
    e.preventDefault();
  };

  const syncComposerState = () => {
    const editor = composerRef.current;
    if (!editor) return;

    setComposerHtml(editor.innerHTML || '');
    setComposerText((editor.textContent || '').replace(/\u00A0/g, ' '));
  };

  const syncFormatState = () => {
    try {      setFormatState({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        strike: document.queryCommandState('strikeThrough'),      });
    } catch {
      // ignore query command state errors
    }
  };

  const runCommand = (command, value = null) => {
    const editor = composerRef.current;
    if (!editor) return;

    editor.focus();
    document.execCommand(command, false, value);
    syncComposerState();
    syncFormatState();
  };
  const keepSelection = (e) => e.preventDefault();

  const insertEmojiAtCursor = (emoji) => {
    const editor = composerRef.current;
    if (!editor) return;

    editor.focus();
    document.execCommand('insertText', false, emoji);
    syncComposerState();
    syncFormatState();
    setRecentEmojis((prev) => {
      const next = [emoji, ...prev.filter((item) => item !== emoji)].slice(0, MAX_RECENT_EMOJIS);
      saveRecentEmojis(next);
      return next;
    });
    setShowEmojiPicker(false);
  };

  const getFilteredEmojis = () => {
    const source = emojiCategory === 'recent'
      ? recentEmojis
      : (EMOJI_CATEGORIES[emojiCategory] || []);

    const q = emojiSearch.trim();
    if (!q) return source;
    return source.filter((emoji) => emoji.includes(q));
  };

  useEffect(() => {
    const handleSelectionChange = () => {
      const editor = composerRef.current;
      if (!editor) return;
      const active = document.activeElement;
      if (active === editor || editor.contains(active)) {
        syncFormatState();
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, []);

  useEffect(() => {
    const closeComposerMenu = () => {
      setComposerContextMenu((prev) => (prev.visible ? { visible: false, x: 0, y: 0 } : prev));
    };

    const closeOnEscape = (e) => {
      if (e.key === 'Escape') {
        closeComposerMenu();
      }
    };

    document.addEventListener('click', closeComposerMenu);
    window.addEventListener('scroll', closeComposerMenu, true);
    window.addEventListener('resize', closeComposerMenu);
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.removeEventListener('click', closeComposerMenu);
      window.removeEventListener('scroll', closeComposerMenu, true);
      window.removeEventListener('resize', closeComposerMenu);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  useEffect(() => {
    const closeContextMenu = () => {
      setContextMenu((prev) => (prev.visible ? { visible: false, x: 0, y: 0, message: null, isIncoming: false } : prev));
    };

    const closeOnEscape = (e) => {
      if (e.key === 'Escape') {
        closeContextMenu();
      }
    };

    document.addEventListener('click', closeContextMenu);
    document.addEventListener('contextmenu', closeContextMenu);
    window.addEventListener('scroll', closeContextMenu, true);
    window.addEventListener('resize', closeContextMenu);
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.removeEventListener('click', closeContextMenu);
      document.removeEventListener('contextmenu', closeContextMenu);
      window.removeEventListener('scroll', closeContextMenu, true);
      window.removeEventListener('resize', closeContextMenu);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  useEffect(() => {
    const closeParticipantMenu = () => {
      setParticipantMenu((prev) => (prev.visible ? { visible: false, x: 0, y: 0, participant: null } : prev));
    };

    const closeOnEscape = (e) => {
      if (e.key === 'Escape') {
        closeParticipantMenu();
      }
    };

    document.addEventListener('click', closeParticipantMenu);
    document.addEventListener('contextmenu', closeParticipantMenu);
    window.addEventListener('scroll', closeParticipantMenu, true);
    window.addEventListener('resize', closeParticipantMenu);
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.removeEventListener('click', closeParticipantMenu);
      document.removeEventListener('contextmenu', closeParticipantMenu);
      window.removeEventListener('scroll', closeParticipantMenu, true);
      window.removeEventListener('resize', closeParticipantMenu);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  useEffect(() => {
    if (!showEmojiPicker) return undefined;

    const handleOutsideClick = (e) => {
      const picker = emojiPickerRef.current;
      const toggle = emojiToggleRef.current;
      if (!picker || !toggle) return;
      if (picker.contains(e.target) || toggle.contains(e.target)) return;
      setShowEmojiPicker(false);
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showEmojiPicker]);

  useEffect(() => () => {
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (!imageViewer.open) return undefined;

    const closeOnEscape = (e) => {
      if (e.key === 'Escape') {
        setImageViewer({ open: false, src: '', alt: '' });
      }
    };

    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [imageViewer.open]);

  if (!chat) {
    return (
      <div className="chat-window empty">
        <div className="empty-state">
          <h2>Выберите чат</h2>
          <p>Выберите один из чатов слева, чтобы начать общение</p>
        </div>
      </div>
    );
  }

  const fullName = `${chat.firstName || ''} ${chat.lastName || ''}`.trim() || 'Пользователь';
  const messageItems = buildMessageItems(messages);

  const sendCurrentMessage = async () => {
    const normalizedHtml = normalizeComposerHtml(composerHtml);
    const replyText = replyTo ? getMessageReplyPreviewText(replyTo) : '';
    const replyMessageId = replyTo ? (replyTo.messageId ?? replyTo.id) : null;
    const replyHref = replyMessageId !== null && replyMessageId !== undefined
      ? `max://message/${encodeURIComponent(String(replyMessageId))}`
      : '';
    const replyHtml = replyText
      ? (replyHref
        ? `<blockquote><a href="${replyHref}">${escapeHtml(replyText)}</a></blockquote>`
        : `<blockquote>${escapeHtml(replyText)}</blockquote>`)
      : '';
    const authorName = `${currentUser?.displayName || currentUser?.name || ''}`.trim();
    const plainEditorText = stripHtml(normalizedHtml).trim();
    const hasHtmlText = Boolean(normalizedHtml);
    const shouldIncludeAuthorPrefix = Boolean(authorName) && (hasHtmlText || Boolean(replyHtml));
    const signedHtmlBody = [
      shouldIncludeAuthorPrefix ? `<b>${escapeHtml(authorName)}:</b>` : '',
      shouldIncludeAuthorPrefix ? '<br><br>' : '',
      replyHtml ? `${replyHtml}<br>` : '',
      hasHtmlText ? normalizedHtml : (replyHtml ? '' : escapeHtml(plainEditorText))
    ].join('');
    const signedHtml = signedHtmlBody.trim();
    const plainTextBody = stripHtml(signedHtml).trim();
    const sentPreviewText = plainTextBody;
    const hasText = Boolean(plainTextBody);
    const hasFile = Boolean(attachedFile);

    if (editingMessageId) {
      if (!hasText) return;
      try {
        setEditLoading(true);
        setEditError('');
        await ApiService.editMessage(editingMessageId, sentPreviewText);
        if (onEditMessage) {
          await onEditMessage();
        }
        if (composerRef.current) {
          composerRef.current.innerHTML = '';
        }
        setComposerHtml('');
        setComposerText('');
        setEditingMessageId(null);
      } catch (err) {
        setEditError(err.message || 'Не удалось отредактировать сообщение');
      } finally {
        setEditLoading(false);
      }
      return;
    }

    if ((hasText || hasFile) && chat && !uploadLoading) {
      try {
        setUploadLoading(true);
        let attachments = null;
        let attachmentType = null;
        if (attachedFile) {
          attachmentType = detectAttachmentType(attachedFile);
          const uploadedAttachment = await ApiService.uploadAttachment(attachedFile, attachmentType);
          if (!uploadedAttachment) {
            throw new Error('Не удалось загрузить файл');
          }
          attachments = [uploadedAttachment];
        }

        const message = {
          chatId: chat.chatId,
          text: hasText ? signedHtml : (hasFile ? 'Вложение' : sentPreviewText),
          format: hasText ? 'html' : null,
          attachments,
          isFromBot: false
        };

        try {
          await ApiService.sendMessage(message);
        } catch (sendErr) {
          // Fallback for strict markdown validation errors in MAX API and expired upload tokens.
          const fallbackMessage = {
            ...message,
            text: sentPreviewText || (hasFile ? 'Вложение' : ''),
            format: null
          };
          if (hasFile && attachedFile) {
            try {
              const retryAttachment = await ApiService.uploadAttachment(attachedFile, attachmentType || detectAttachmentType(attachedFile));
              if (retryAttachment) {
                fallbackMessage.attachments = [retryAttachment];
              }
            } catch {
              // If retry upload fails, keep first attachment payload and let send attempt return the real error.
            }
          }
          await ApiService.sendMessage(fallbackMessage);
        }
        const sentText = sentPreviewText || (attachedFile ? 'Вложение' : '');
        if (composerRef.current) {
          composerRef.current.innerHTML = '';
        }
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setComposerHtml('');
        setComposerText('');
        setAttachedFile(null);
        setReplyTo(null);
        onSendMessage(chat.chatId, sentText);
      } catch (err) {
        console.error('Send message error:', err);
        setEditError(err.message || 'Не удалось отправить сообщение');
      } finally {
        setUploadLoading(false);
      }
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    await sendCurrentMessage();
  };

  const handleComposerKeyDown = async (e) => {
    if (e.key !== 'Enter') return;
    if (e.nativeEvent?.isComposing) return;

    if (e.ctrlKey) {
      e.preventDefault();
      document.execCommand('insertLineBreak');
      syncComposerState();
      syncFormatState();
      return;
    }

    e.preventDefault();
    await sendCurrentMessage();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.size > MAX_UPLOAD_SIZE_BYTES) {
      setEditError('Файл больше 100 МБ. Выберите файл меньшего размера.');
      setAttachedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    setEditError('');
    setAttachedFile(file || null);
  };

  const handleRemoveFile = () => {
    setAttachedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const insertTemplateToComposer = (text) => {
    const template = String(text || '');
    if (!template) return;

    setEditingMessageId(null);
    setReplyTo(null);

    const html = escapeHtml(template).replace(/\n/g, '<br>');
    if (composerRef.current) {
      composerRef.current.innerHTML = html;
      composerRef.current.focus();

      const selection = window.getSelection();
      if (selection) {
        const range = document.createRange();
        range.selectNodeContents(composerRef.current);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }

    setComposerHtml(html);
    setComposerText(template);
  };

  const handleDeleteMessage = async (messageId) => {
    if (!messageId) return;
    try {
      if (onDeleteMessage) {
        await onDeleteMessage(messageId);
        return;
      }
      await ApiService.deleteMessage(messageId);
    } catch (err) {
      console.error('Delete message error:', err);
    }
  };

  const startEditMessage = (message) => {
    setEditError('');
    setEditingMessageId(message.messageId || message.id);
    setReplyTo(null);
    setAttachedFile(null);

    const authorName = `${currentUser?.displayName || currentUser?.name || ''}`.trim();
    const sourceText = stripHtml(decodeHtmlEntities(message?.text || '')).trim();
    const editableText = stripAuthorPrefix(sourceText, authorName);
    if (composerRef.current) {
      composerRef.current.innerHTML = escapeHtml(editableText).replace(/\n/g, '<br>');
    }
    setComposerHtml(escapeHtml(editableText).replace(/\n/g, '<br>'));
    setComposerText(editableText);
    composerRef.current?.focus();
  };

  const cancelEditMessage = () => {
    setEditingMessageId(null);
    setEditError('');
    if (composerRef.current) {
      composerRef.current.innerHTML = '';
    }
    setComposerHtml('');
    setComposerText('');
  };

  const handleRequestContact = async () => {
    if (!chat?.chatId || requestContactLoading) return;

    try {
      setRequestContactLoading(true);
      setEditError('');
      await ApiService.requestContact(chat.chatId);
      if (onEditMessage) {
        await onEditMessage();
      }
    } catch (err) {
      setEditError(err.message || 'Не удалось запросить номер телефона');
    } finally {
      setRequestContactLoading(false);
    }
  };

  const openMessageContextMenu = (e, msg, isIncoming) => {
    if (!msg) return;
    e.preventDefault();
    e.stopPropagation();

    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      message: msg,
      isIncoming
    });
  };

  const closeMessageContextMenu = () => {
    setContextMenu({ visible: false, x: 0, y: 0, message: null, isIncoming: false });
  };

  const openComposerContextMenu = (e) => {
    const editor = composerRef.current;
    if (!editor) return;

    const selection = window.getSelection();
    const selectedText = selection?.toString()?.trim() || '';
    const anchorNode = selection?.anchorNode || null;
    const focusNode = selection?.focusNode || null;
    const insideEditor = Boolean(
      anchorNode
      && focusNode
      && editor.contains(anchorNode)
      && editor.contains(focusNode)
    );

    if (!insideEditor || !selectedText) {
      setComposerContextMenu({ visible: false, x: 0, y: 0 });
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    const menuWidth = 176;
    const menuHeight = 182; // 4 items + paddings
    const viewportWidth = window.innerWidth || 0;
    const viewportHeight = window.innerHeight || 0;
    const horizontalPadding = 8;
    const verticalPadding = 8;

    const x = Math.min(
      Math.max(horizontalPadding, e.clientX),
      Math.max(horizontalPadding, viewportWidth - menuWidth - horizontalPadding)
    );
    const y = Math.min(
      Math.max(verticalPadding, e.clientY - menuHeight),
      Math.max(verticalPadding, viewportHeight - menuHeight - verticalPadding)
    );

    setComposerContextMenu({ visible: true, x, y });
  };

  const applyComposerInlineStyle = (command) => {
    runCommand(command);
    setComposerContextMenu({ visible: false, x: 0, y: 0 });
  };

  const handleContextEdit = () => {
    if (!contextMenu.message) return;
    startEditMessage(contextMenu.message);
    closeMessageContextMenu();
  };

  const handleContextDelete = () => {
    const messageId = contextMenu.message?.messageId || contextMenu.message?.id;
    closeMessageContextMenu();
    if (messageId) {
      handleDeleteMessage(messageId);
    }
  };

  const handleContextReply = () => {
    if (!contextMenu.message) return;
    setReplyTo(contextMenu.message);
    closeMessageContextMenu();
    composerRef.current?.focus();
  };

  const handleNavigateToMessage = (targetId) => {
    if (!targetId && targetId !== 0) return;
    const normalizedId = String(targetId);
    const targetNode = messageNodeMapRef.current.get(normalizedId);
    if (!targetNode) return;

    targetNode.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedMessageId(normalizedId);

    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedMessageId('');
      highlightTimeoutRef.current = null;
    }, 1800);
  };

  const openImageViewer = (src, alt) => {
    if (!src) return;
    setImageViewer({ open: true, src, alt: alt || 'Изображение' });
  };

  const closeImageViewer = () => {
    setImageViewer({ open: false, src: '', alt: '' });
  };

  const openParticipantContextMenu = (e, participant) => {
    if (!currentUser?.accessAllChats || !participant) return;
    e.preventDefault();
    e.stopPropagation();
    setParticipantMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      participant
    });
  };

  const handleKickParticipant = async () => {
    const participant = participantMenu.participant;
    setParticipantMenu({ visible: false, x: 0, y: 0, participant: null });
    if (!participant || !chat?.chatId || !onKickFromDialog) return;

    try {
      await onKickFromDialog(chat, participant);
      const items = await ApiService.getDialogParticipants(chat.chatId);
      setDialogParticipants(Array.isArray(items) ? items : []);
    } catch (err) {
      setEditError(err.message || 'Не удалось вывести пользователя из диалога');
    }
  };

  const openRequestContactConfirm = () => {
    if (!chat?.chatId || requestContactLoading) return;
    setEditError('');
    setRequestContactConfirmOpen(true);
  };

  const closeRequestContactConfirm = () => {
    if (requestContactLoading) return;
    setRequestContactConfirmOpen(false);
  };

  const confirmRequestContact = async () => {
    if (requestContactLoading) return;
    await handleRequestContact();
    setRequestContactConfirmOpen(false);
  };

  const handleMessagesScroll = () => {
    const list = messagesListRef.current;
    if (!list) return;
    if (list.scrollTop > 60) return;
    if (!hasMoreMessages || loadingOlderMessages) return;
    onLoadOlderMessages?.();
  };

  return (
    <div className="chat-window">
      <div className="chat-header">
        <div className="chat-window-info">
          <div className="chat-window-avatar">
            {chat.icon?.url ? (
              <img src={chat.icon.url} alt="Avatar" />
            ) : (
              (chat.firstName?.charAt(0) || chat.lastName?.charAt(0) || 'U').toUpperCase()
            )}
          </div>
          <div className="chat-meta-row">
            <h2 className="chat-title">{fullName}</h2>
            <p className="chat-window-status">В сети</p>
            <button
              type="button"
              className="phone-button"
              onClick={openRequestContactConfirm}
              disabled={requestContactLoading}
            >
              {requestContactLoading ? 'Отправка...' : 'Телефон'}
            </button>
          </div>
        </div>
      </div>

      {requestContactConfirmOpen && (
        <div className="chat-confirm-overlay" role="dialog" aria-modal="true" aria-label="Подтверждение запроса телефона">
          <div className="chat-confirm-card" onClick={(e) => e.stopPropagation()}>
            <div className="chat-confirm-title">Предупреждение</div>
            <div className="chat-confirm-text">Функция запросит номер телефона у клиента</div>
            <div className="chat-confirm-actions">
              <button type="button" className="chat-confirm-btn secondary" onClick={closeRequestContactConfirm} disabled={requestContactLoading}>Отмена</button>
              <button type="button" className="chat-confirm-btn primary" onClick={confirmRequestContact} disabled={requestContactLoading}>
                {requestContactLoading ? 'Отправка...' : 'Подтвердить'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="messages-container">
        {error && <div className="error-message">{error}</div>}
        {editError && <div className="error-message">{editError}</div>}

        <div className="messages" ref={messagesListRef} onScroll={handleMessagesScroll}>
          {loadingOlderMessages && (
            <div className="messages-date-divider">
              <span className="messages-date-pill">Загрузка истории...</span>
            </div>
          )}
          {messages.length === 0 ? (
            <div className="no-messages">
              <p>Нет сообщений</p>
              <small>Начните разговор с отправки сообщения</small>
            </div>
          ) : (
            messageItems.map((item, index) => {
              if (item.type === 'divider') {
                return (
                  <div key={`divider-${item.key}-${index}`} className="messages-date-divider">
                    <span className="messages-date-pill">{item.label}</span>
                  </div>
                );
              }

              const msg = item.message;
              const messageId = msg.messageId || msg.id;
              const messageIdKey = messageId !== null && messageId !== undefined ? String(messageId) : '';
              const isIncoming = Boolean(msg.isFromBot || msg.userId);
              const isUnreadIncoming = isIncoming && isMessageUnread(msg, lastReadAt);
              const isForwarded = Boolean(msg.isForwarded) || isForwardedMessage(msg);
              const hasTextContent = Boolean(stripHtml(msg.text || '').trim());

              return (
                <div
                  key={messageId || index}
                  className={`message ${isIncoming ? 'incoming' : 'outgoing'} ${highlightedMessageId && highlightedMessageId === messageIdKey ? 'message-target' : ''}`}
                  ref={(node) => {
                    if (!messageIdKey) return;
                    if (node) {
                      messageNodeMapRef.current.set(messageIdKey, node);
                    } else {
                      messageNodeMapRef.current.delete(messageIdKey);
                    }
                  }}
                >
                  {isIncoming && (
                    <div className="message-avatar">
                      {chat.icon?.url ? (
                        <img src={chat.icon.url} alt="Avatar" />
                      ) : (
                        (chat.firstName?.charAt(0) || chat.lastName?.charAt(0) || 'U').toUpperCase()
                      )}
                    </div>
                  )}

                  <div
                    className="message-content"
                    onContextMenu={(e) => openMessageContextMenu(e, msg, isIncoming)}
                  >
                    {isForwarded && (
                      <div className="message-forwarded-label">Пересланное</div>
                    )}
                    {hasTextContent ? (
                      <div className="message-text-row">
                        <MessageText text={msg.text} onNavigateToMessage={handleNavigateToMessage} />
                        <span className={`message-time ${isUnreadIncoming ? 'unread' : ''}`}>
                          {formatMessageTime(parseServerDate(msg.timestampMs ?? msg.timestamp))}
                          {isUnreadIncoming ? ' •' : ''}
                        </span>
                      </div>
                    ) : null}

                    {msg.attachments && (
                      <div className="attachments">
                        {renderAttachments(msg.attachments, openImageViewer)}
                      </div>
                    )}

                    {!hasTextContent && (
                      <span className={`message-time ${isUnreadIncoming ? 'unread' : ''}`}>
                        {formatMessageTime(parseServerDate(msg.timestampMs ?? msg.timestamp))}
                        {isUnreadIncoming ? ' •' : ''}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {contextMenu.visible && (
        <div
          className="message-context-menu"
          style={{ top: `${contextMenu.y}px`, left: `${contextMenu.x}px` }}
          onClick={(e) => e.stopPropagation()}
        >
          <button type="button" className="message-context-item" onClick={handleContextReply}>
            Ответить
          </button>
          {!contextMenu.isIncoming && canDeleteMessageByAge(contextMenu.message) && (
            <button type="button" className="message-context-item" onClick={handleContextEdit}>
              Редактировать
            </button>
          )}
          {canDeleteMessageByAge(contextMenu.message) && (
            <button type="button" className="message-context-item danger" onClick={handleContextDelete}>
              Удалить
            </button>
          )}
        </div>
      )}

      {participantMenu.visible && currentUser?.accessAllChats && (
        <div
          className="message-context-menu"
          style={{ top: `${participantMenu.y}px`, left: `${participantMenu.x}px` }}
          onClick={(e) => e.stopPropagation()}
        >
          <button type="button" className="message-context-item danger" onClick={handleKickParticipant}>
            Вывести из диалога
          </button>
        </div>
      )}

      {composerContextMenu.visible && (
        <div
          className="composer-context-menu"
          style={{ top: `${composerContextMenu.y}px`, left: `${composerContextMenu.x}px` }}
          onClick={(e) => e.stopPropagation()}
        >
          <button type="button" className="composer-context-item" onMouseDown={keepSelection} onClick={() => applyComposerInlineStyle('bold')}>
            Жирный
          </button>
          <button type="button" className="composer-context-item" onMouseDown={keepSelection} onClick={() => applyComposerInlineStyle('italic')}>
            Курсив
          </button>
          <button type="button" className="composer-context-item" onMouseDown={keepSelection} onClick={() => applyComposerInlineStyle('underline')}>
            Подчеркнутый
          </button>
          <button type="button" className="composer-context-item" onMouseDown={keepSelection} onClick={() => applyComposerInlineStyle('strikeThrough')}>
            Зачеркнутый
          </button>
        </div>
      )}

      {imageViewer.open && (
        <div className="image-viewer-overlay" onClick={closeImageViewer}>
          <button
            type="button"
            className="image-viewer-close"
            onClick={closeImageViewer}
            aria-label="Закрыть просмотр изображения"
          >
            ?
          </button>
          <img
            className="image-viewer-image"
            src={imageViewer.src}
            alt={imageViewer.alt}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <form className="message-input" onSubmit={handleSendMessage}>
        {editingMessageId && (
          <div className="reply-preview">
            <div className="reply-preview-label">Редактирование сообщения</div>
            <button
              type="button"
              className="reply-preview-close"
              onClick={cancelEditMessage}
              title="Отменить редактирование"
            >
              ?
            </button>
          </div>
        )}
        {replyTo && (
          <div className="reply-preview">
            <div className="reply-preview-label">Ответ на сообщение</div>
            <div className="reply-preview-text">{getMessageReplyPreviewText(replyTo)}</div>
            <button
              type="button"
              className="reply-preview-close"
              onClick={() => setReplyTo(null)}
              title="Отменить ответ"
            >
              ?
            </button>
          </div>
        )}
        {attachedFile && (
          <div className="file-preview">
            <div className="file-preview-name">{attachedFile.name}</div>
            <button type="button" className="file-remove-btn" onClick={handleRemoveFile} disabled={uploadLoading}>
              ?
            </button>
          </div>
        )}
        <div className="format-toolbar">
          <div className="dialog-participants-inline" title="Пользователи в диалоге">
            {dialogParticipants.map((participant) => (
              <div
                key={participant.userId}
                className={`dialog-participant-avatar ${participant.isOnline ? 'online' : 'offline'}`}
                title={`${participant.displayName || participant.username}${participant.connections > 1 ? ` (${participant.connections})` : ''}`}
                onContextMenu={(e) => openParticipantContextMenu(e, participant)}
              >
                {participant.avatarUrl ? (
                  <img src={participant.avatarUrl} alt={participant.displayName || participant.username || 'user'} />
                ) : (
                  <span>{getAvatarInitials(participant.username)}</span>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            className="invite-dialog-btn"
            title="Пригласить пользователя"
            onClick={() => onInviteToDialog?.(chat)}
          >
            ПРИГЛАСИТЬ В ДИАЛОГ
          </button>
          <button
            type="button"
            className="quick-template-btn"
            title="Подставить контрольный вопрос"
            onClick={() => insertTemplateToComposer('Подскажите, остались ли у вас ещё вопросы?')}
          >
            КОНТРОЛЬНЫЙ ?
          </button>
          <button
            type="button"
            className="quick-template-btn"
            title="Подставить текст закрытия"
            onClick={() => insertTemplateToComposer('Закрываем тикет. Если у вас появятся вопросы — пожалуйста, обращайтесь. Будем рады помочь. Всего доброго.')}
          >
            ЗАКРЫТЬ
          </button>
          {!currentUser?.accessAllChats && (
            <button
              type="button"
              className="exit-dialog-btn"
              title="Покинуть диалог"
              onClick={() => onLeaveDialog?.(chat)}
            >
              ВЫЙТИ ИЗ ДИАЛОГА
            </button>
          )}
        </div>
        <div className="message-compose-row">
          <div className="composer-editor-wrap">
            <button
              type="button"
              className="composer-resize-handle"
              onMouseDown={handleComposerResizeStart}
              onDragStart={(e) => e.preventDefault()}
              aria-label="Изменить высоту поля ввода"
              title="Потяните вверх или вниз, чтобы изменить высоту"
            />
            <label className="attach-file-btn attach-file-btn-inside" title="Прикрепить файл">
              <FiPaperclip />
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                disabled={uploadLoading || Boolean(editingMessageId)}
              />
            </label>
            <div
              ref={composerRef}
              className="composer-editor"
              style={{ height: `${composerHeight}px` }}
              contentEditable={Boolean(chat)}
              suppressContentEditableWarning
              data-placeholder="Введите сообщение..."
              onInput={syncComposerState}
              onKeyDown={handleComposerKeyDown}
              onContextMenu={openComposerContextMenu}
            />
          </div>
          <div className="emoji-picker-wrap">
            <button
              ref={emojiToggleRef}
              type="button"
              className="emoji-btn"
              title="Смайлы"
              onMouseDown={keepSelection}
              onClick={() => setShowEmojiPicker((prev) => !prev)}
            >
              <FiSmile />
            </button>
            {showEmojiPicker && (
              <div ref={emojiPickerRef} className="emoji-picker">
                <div className="emoji-tabs">
                  {EMOJI_CATEGORY_TABS.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      className={`emoji-tab ${emojiCategory === tab.key ? 'active' : ''}`}
                      onMouseDown={keepSelection}
                      onClick={() => setEmojiCategory(tab.key)}
                      title={tab.key}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                <div className="emoji-search-wrap">
                  <input
                    type="text"
                    className="emoji-search"
                    placeholder="Поиск эмодзи..."
                    value={emojiSearch}
                    onChange={(e) => setEmojiSearch(e.target.value)}
                  />
                </div>
                <div className="emoji-grid">
                  {getFilteredEmojis().length === 0 ? (
                    <div className="emoji-empty">Ничего не найдено</div>
                  ) : (
                    getFilteredEmojis().map((emoji) => (
                      <button
                        key={`${emojiCategory}-${emoji}`}
                        type="button"
                        className="emoji-item"
                        onMouseDown={keepSelection}
                        onClick={() => insertEmojiAtCursor(emoji)}
                      >
                        {emoji}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          <button className="send-btn" type="submit" disabled={(!composerText.trim() && !attachedFile) || uploadLoading || editLoading}>
            <FiSend />
          </button>
        </div>
      </form>
    </div>
  );
}

function MessageText({ text, onNavigateToMessage }) {
  const html = sanitizeMessageHtml(text || '');
  const { replyHtml, bodyHtml } = splitReplyFromHtml(html);

  const handleClick = (e) => {
    const anchor = e.target?.closest?.('a[href]');
    if (!anchor) return;
    const href = anchor.getAttribute('href') || '';
    const match = href.match(/^max:\/\/message\/([^/?#]+)/i);
    if (!match) return;

    e.preventDefault();
    onNavigateToMessage?.(decodeURIComponent(match[1]));
  };

  return (
    <div className="message-text" onClick={handleClick}>
      {replyHtml ? (
        <div className="message-reply-quote" dangerouslySetInnerHTML={{ __html: replyHtml }} />
      ) : null}
      {bodyHtml ? (
        <div className="message-body-text" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
      ) : null}
      {!replyHtml && !bodyHtml ? (
        <div dangerouslySetInnerHTML={{ __html: html }} />
      ) : null}
    </div>
  );
}

function splitReplyFromHtml(html) {
  if (!html) {
    return { replyHtml: '', bodyHtml: '' };
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div id="root">${html}</div>`, 'text/html');
    const root = doc.getElementById('root');
    if (!root) {
      return { replyHtml: '', bodyHtml: html };
    }

    const firstElement = Array.from(root.childNodes).find((n) => n.nodeType === Node.ELEMENT_NODE);
    if (!(firstElement instanceof HTMLElement) || firstElement.tagName !== 'BLOCKQUOTE') {
      return { replyHtml: '', bodyHtml: html };
    }

    const replyHtml = firstElement.outerHTML;
    firstElement.remove();

    // Remove one leading <br> separator between quote and message body.
    if (root.firstChild && root.firstChild.nodeType === Node.ELEMENT_NODE && root.firstChild.nodeName === 'BR') {
      root.removeChild(root.firstChild);
    } else if (root.firstChild && root.firstChild.nodeType === Node.TEXT_NODE) {
      root.firstChild.textContent = (root.firstChild.textContent || '').replace(/^\s+/, '');
    }

    const bodyHtml = root.innerHTML;
    return { replyHtml, bodyHtml };
  } catch {
    return { replyHtml: '', bodyHtml: html };
  }
}

function sanitizeMessageHtml(raw) {
  if (!raw) {
    return '';
  }

  const normalizedRaw = decodeHtmlEntities(String(raw));
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${normalizedRaw}</div>`, 'text/html');
  const root = doc.body.firstChild;
  if (!root) {
    return '';
  }

  sanitizeNode(root, doc);
  return root.innerHTML;
}

function decodeHtmlEntities(value) {
  if (!value) {
    return '';
  }

  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

function sanitizeNode(node, doc) {
  if (!node?.childNodes) {
    return;
  }

  Array.from(node.childNodes).forEach((child) => {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const tag = child.tagName;

      if (!ALLOWED_TAGS.has(tag)) {
        const textNode = doc.createTextNode(child.textContent || '');
        node.replaceChild(textNode, child);
        return;
      }

      const attrs = Array.from(child.attributes || []);
      attrs.forEach((attr) => {
        if (tag === 'A' && attr.name.toLowerCase() === 'href') {
          return;
        }
        child.removeAttribute(attr.name);
      });

      if (tag === 'A') {
        const href = child.getAttribute('href') || '';
        if (!/^https?:\/\//i.test(href) && !/^max:\/\/user\//i.test(href) && !/^max:\/\/message\//i.test(href)) {
          child.replaceWith(doc.createTextNode(child.textContent || ''));
          return;
        }

        child.setAttribute('target', '_blank');
        child.setAttribute('rel', 'noopener noreferrer');
      }

      sanitizeNode(child, doc);
      return;
    }

    if (child.nodeType === Node.COMMENT_NODE) {
      node.removeChild(child);
    }
  });
}

function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}


function detectAttachmentType(file) {
  const mime = (file?.type || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'file';
}

function stripHtml(html) {
  if (!html) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  return (doc.body.textContent || '').replace(/\u00A0/g, ' ');
}

function normalizeComposerHtml(html) {
  if (!html) return '';

  return html
    .replace(/<div><br><\/div>/gi, '<br>')
    .replace(/<div>/gi, '')
    .replace(/<\/div>/gi, '<br>')
    .replace(/<p>/gi, '')
    .replace(/<\/p>/gi, '<br>')
    .replace(/(?:<br>\s*){3,}/gi, '<br><br>')
    .replace(/^(<br>\s*)+|(<br>\s*)+$/gi, '')
    .trim();
}

function formatMessageTime(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit'
  });
}

function parseServerDate(rawValue) {
  if (!rawValue && rawValue !== 0) return null;

  if (rawValue instanceof Date) {
    return Number.isNaN(rawValue.getTime()) ? null : rawValue;
  }

  if (typeof rawValue === 'number') {
    const ms = rawValue < 1e12 ? rawValue * 1000 : rawValue;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof rawValue === 'string') {
    const value = rawValue.trim();
    if (!value) return null;

    if (/^\d+$/.test(value)) {
      return parseServerDate(Number(value));
    }

    // If timestamp has no timezone, treat it as UTC from server.
    const localIsoMatch = value.match(
      /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,7}))?$/
    );
    if (localIsoMatch) {
      const [, y, m, d, hh, mm, ss, ms = '0'] = localIsoMatch;
      const date = new Date(Date.UTC(
        Number(y),
        Number(m) - 1,
        Number(d),
        Number(hh),
        Number(mm),
        Number(ss),
        Number(ms.slice(0, 3).padEnd(3, '0'))
      ));
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
}

function renderAttachments(attachmentsData, onImageClick) {
  if (!attachmentsData) return null;

  try {
    let attachments = attachmentsData;

    if (typeof attachmentsData === 'string') {
      try {
        attachments = JSON.parse(attachmentsData);
      } catch {
        return null;
      }
    }

    if (!Array.isArray(attachments)) {
      return null;
    }

    return attachments.map((attachment, idx) => {
      const type = attachment?.type || 'unknown';
      const payload = attachment?.payload;
      const normalizedType = String(type || '').toLowerCase();

      if (isAudioAttachmentType(normalizedType)) {
        return renderAudioAttachment(idx, payload);
      }

      if (isVideoAttachmentType(normalizedType)) {
        return renderVideoAttachment(idx, payload);
      }

      if (normalizedType === 'image') {
        const imageUrl = extractAttachmentUrl(payload, true) || extractAttachmentUrl(payload, false);

        if (!imageUrl) {
          return <div key={idx} style={{ marginTop: '8px', color: '#666' }}>Изображение (нет URL)</div>;
        }

        return (
          <button
            key={idx}
            type="button"
            className="attachment-image-btn"
            onClick={() => onImageClick?.(imageUrl, payload?.title || 'Image')}
            title="Открыть изображение"
          >
            <img
              className="attachment-image"
              src={imageUrl}
              alt={payload?.title || 'Image'}
            />
          </button>
        );
      }

      if (isWebLinkAttachment(attachment, payload, normalizedType)) {
        return renderLinkAttachment(idx, payload, attachment);
      }

      if (isFileLikeAttachmentType(normalizedType)) {
        return renderFileAttachment(idx, attachment, payload);
      }

      switch (normalizedType) {
        case 'link':
        case 'url':
          return renderLinkAttachment(idx, payload, attachment);

        case 'contact': {
          const contact = extractContact(payload);
          if (!contact) {
            return (
              <div key={idx} style={{ marginTop: '8px', color: '#666' }}>
                Контакт
              </div>
            );
          }

          return (
            <div
              key={idx}
              style={{
                marginTop: '8px',
                padding: '10px 12px',
                borderRadius: '8px',
                background: 'rgba(0,0,0,0.05)'
              }}
            >
              <div style={{ fontWeight: 600 }}>{contact.name || 'Контакт'}</div>
              {contact.phone && (
                <a href={`tel:${contact.phone}`} style={{ color: '#0b57d0', textDecoration: 'none' }}>
                  {contact.phone}
                </a>
              )}
            </div>
          );
        }

        default:
          return renderFileAttachment(idx, attachment, payload);
      }
    });
  } catch {
    return null;
  }
}

function renderFileAttachment(idx, attachment, payload) {
  const fileUrl = extractAttachmentUrl(payload, false) || extractAttachmentUrl(attachment, false);
  const fileMeta = extractFileMeta(attachment, payload, fileUrl);

  if (!fileUrl) {
    return (
      <div key={idx} className="attachment-file">
        <div className="attachment-file-icon" aria-hidden="true">{renderFileTypeIcon(fileMeta)}</div>
        <div className="attachment-file-main">
          <div className="attachment-file-name">{fileMeta.name || 'Файл отправлен'}</div>
        </div>
      </div>
    );
  }

  return (
    <div key={idx} className="attachment-file">
      <div className="attachment-file-icon" aria-hidden="true">{renderFileTypeIcon(fileMeta)}</div>
      <div className="attachment-file-main">
        <div className="attachment-file-name">{fileMeta.name}</div>
        <a
          className="attachment-file-download"
          href={fileUrl}
          download={fileMeta.name || 'file'}
        >
          Скачать{fileMeta.sizeLabel ? ` (${fileMeta.sizeLabel})` : ''}
        </a>
      </div>
    </div>
  );
}

function renderLinkAttachment(idx, payload, attachment) {
  const linkUrl = String(
    payload?.url
    || payload?.href
    || attachment?.url
    || extractAttachmentUrl(payload, false)
    || ''
  ).trim();
  const linkTitle = String(
    payload?.title
    || payload?.name
    || attachment?.title
    || attachment?.name
    || linkUrl
    || 'Ссылка'
  ).trim();

  if (!linkUrl) {
    return (
      <div key={idx} style={{ marginTop: '8px', fontSize: '0.9em', color: '#999' }}>
        Ссылка
      </div>
    );
  }

  return (
    <div key={idx} style={{ marginTop: '8px' }}>
      <a href={linkUrl} target="_blank" rel="noreferrer">
        {linkTitle}
      </a>
    </div>
  );
}

function extractContact(payload) {
  if (!payload) return null;

  const firstName = payload?.max_info?.first_name || '';
  const lastName = payload?.max_info?.last_name || '';
  const maxName = `${firstName} ${lastName}`.trim();

  const vcf = payload?.vcf_info || '';
  const telMatch = typeof vcf === 'string'
    ? vcf.match(/TEL[^:]*:([+\d][\d\s()-]*)/i)
    : null;
  const fnMatch = typeof vcf === 'string'
    ? vcf.match(/FN:(.+)/i)
    : null;

  const phone = telMatch?.[1]?.trim() || '';
  const vcfName = fnMatch?.[1]?.trim() || '';

  const name = maxName || vcfName || '';
  if (!name && !phone) return null;

  return { name, phone };
}

function extractFileMeta(attachment, payload, url) {
  const attachmentPayload = payload && typeof payload === 'object'
    ? payload
    : (attachment?.payload && typeof attachment.payload === 'object' ? attachment.payload : {});

  const rawName = String(
    attachment?.title
    || attachment?.file_name
    || attachment?.filename
    || attachment?.name
    || attachmentPayload?.title
    || attachmentPayload?.file_name
    || attachmentPayload?.filename
    || attachmentPayload?.name
    || attachmentPayload?.original_name
    || attachmentPayload?.display_name
    || attachmentPayload?.file?.name
    || attachmentPayload?.document?.name
    || attachmentPayload?.media?.name
    || ''
  ).trim();
  const nameFromUrl = extractFileNameFromUrl(url);
  const mimeType = String(
    attachment?.mime_type
    || attachment?.content_type
    || attachmentPayload?.mime_type
    || attachmentPayload?.content_type
    || attachmentPayload?.mime
    || attachmentPayload?.type
    || attachmentPayload?.file?.mime_type
    || attachmentPayload?.document?.mime_type
    || ''
  ).trim().toLowerCase();
  const fileName = rawName || nameFromUrl || inferFileNameByMime(mimeType) || 'Файл';
  const extension = (fileName.split('.').pop() || '').toLowerCase();
  const sizeCandidate = parseFileSize(
    attachment?.size
    ?? attachment?.file_size
    ?? attachment?.bytes
    ?? attachment?.content_length
    ?? attachmentPayload?.size
    ?? attachmentPayload?.file_size
    ?? attachmentPayload?.bytes
    ?? attachmentPayload?.content_length
    ?? attachmentPayload?.length
    ?? attachmentPayload?.file?.size
    ?? attachmentPayload?.document?.size
    ?? attachmentPayload?.media?.size
    ?? 0
  );

  return {
    name: fileName,
    iconExtension: getFileIconExtension(extension, mimeType),
    iconLabel: getFileIconByExtension(extension, mimeType),
    sizeLabel: Number.isFinite(sizeCandidate) && sizeCandidate > 0
      ? formatFileSize(sizeCandidate)
      : ''
  };
}

function parseFileSize(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string') {
    const cleaned = value.trim().replace(',', '.');
    if (!cleaned) return 0;
    const numeric = Number(cleaned);
    return Number.isFinite(numeric) ? numeric : 0;
  }
  return 0;
}

function renderFileTypeIcon(fileMeta) {
  const extension = String(fileMeta?.iconExtension || '').toLowerCase();
  if (extension) {
    const preset = defaultStyles[extension] || {};
    return <FileIcon extension={extension} {...preset} />;
  }

  return (
    <span className="attachment-file-icon-fallback">
      {fileMeta?.iconLabel || 'FILE'}
    </span>
  );
}
function extractFileNameFromUrl(url) {
  try {
    if (!url) return '';
    const clean = String(url).split('?')[0];
    const candidate = decodeURIComponent(clean.split('/').pop() || '').trim();
    return candidate || '';
  } catch {
    return '';
  }
}

function getFileIconByExtension(ext, mimeType = '') {
  const value = String(ext || '').toLowerCase();
  if (['doc', 'docx', 'rtf', 'odt'].includes(value)) return 'DOC';
  if (['xls', 'xlsx', 'csv', 'ods'].includes(value)) return 'XLS';
  if (['ppt', 'pptx', 'odp'].includes(value)) return 'PPT';
  if (['pdf'].includes(value)) return 'PDF';
  if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz'].includes(value)) return 'ZIP';
  if (['txt', 'md', 'log', 'ini', 'json', 'xml', 'yaml', 'yml'].includes(value)) return 'TXT';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(value)) return 'IMG';
  if (['mp4', 'webm', 'mov', 'mkv', 'avi'].includes(value)) return 'VID';
  if (['mp3', 'ogg', 'wav', 'm4a', 'aac', 'flac'].includes(value)) return 'AUD';
  if (String(mimeType || '').startsWith('video/')) return 'VID';
  if (String(mimeType || '').startsWith('audio/')) return 'AUD';
  if (String(mimeType || '').startsWith('image/')) return 'IMG';
  if (String(mimeType || '').includes('pdf')) return 'PDF';
  if (String(mimeType || '').includes('word') || String(mimeType || '').includes('officedocument.word')) return 'DOC';
  if (String(mimeType || '').includes('excel') || String(mimeType || '').includes('spreadsheet')) return 'XLS';
  return 'FILE';
}

function getFileIconExtension(ext, mimeType = '') {
  const value = String(ext || '').toLowerCase();
  if (value) return value;

  const mime = String(mimeType || '').toLowerCase();
  if (mime.includes('pdf')) return 'pdf';
  if (mime.includes('word')) return 'doc';
  if (mime.includes('excel') || mime.includes('spreadsheet')) return 'xls';
  if (mime.includes('powerpoint') || mime.includes('presentation')) return 'ppt';
  if (mime.includes('zip') || mime.includes('rar') || mime.includes('7z') || mime.includes('tar') || mime.includes('gzip')) return 'zip';
  if (mime.startsWith('image/')) return 'png';
  if (mime.startsWith('video/')) return 'mp4';
  if (mime.startsWith('audio/')) return 'mp3';
  if (mime.startsWith('text/')) return 'txt';
  return '';
}
function inferFileNameByMime(mimeType) {
  const mime = String(mimeType || '').toLowerCase();
  if (!mime) return '';
  if (mime.includes('pdf')) return 'document.pdf';
  if (mime.includes('word')) return 'document.doc';
  if (mime.includes('excel') || mime.includes('spreadsheet')) return 'table.xls';
  if (mime.includes('zip') || mime.includes('rar') || mime.includes('7z') || mime.includes('tar') || mime.includes('gzip')) return 'archive.zip';
  if (mime.startsWith('text/')) return 'document.txt';
  return '';
}

function isFileLikeAttachmentType(normalizedType) {
  const type = String(normalizedType || '').toLowerCase();
  if (!type) return false;
  if (
    type === 'contact'
    || type === 'image'
    || type === 'video'
    || type === 'audio'
    || type === 'voice'
    || type === 'ptt'
    || type === 'link'
    || type === 'url'
  ) {
    return false;
  }
  return true;
}

function isWebLinkAttachment(attachment, payload, normalizedType) {
  const type = String(normalizedType || '').toLowerCase();
  if (type === 'link' || type === 'url' || type === 'share' || type === 'website' || type === 'page') {
    return true;
  }

  const directUrl = String(
    payload?.url
    || payload?.href
    || attachment?.url
    || ''
  ).trim();

  if (!isHttpUrl(directUrl)) {
    return false;
  }
  if (hasImageHint(directUrl)) {
    return false;
  }

  const knownFileName = String(
    attachment?.file_name
    || attachment?.filename
    || payload?.file_name
    || payload?.filename
    || ''
  ).trim();
  const knownFileSize = parseFileSize(
    attachment?.size
    ?? attachment?.file_size
    ?? payload?.size
    ?? payload?.file_size
    ?? 0
  );
  const mime = String(
    attachment?.mime_type
    || attachment?.content_type
    || payload?.mime_type
    || payload?.content_type
    || ''
  ).toLowerCase();

  const looksLikeRealFile = Boolean(knownFileName) || knownFileSize > 0 || (mime && !mime.startsWith('text/html'));
  return !looksLikeRealFile;
}

function formatFileSize(bytes) {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return '';
  const units = ['Б', 'КБ', 'МБ', 'ГБ'];
  let value = size;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = value >= 10 ? value.toFixed(0) : value.toFixed(1);
  return `${rounded} ${units[unit]}`;
}

function buildMessageItems(messages) {
  const items = [];
  let lastDateKey = '';

  for (const msg of messages || []) {
    const date = parseServerDate(msg?.timestampMs ?? msg?.timestamp);
    const dateKey = getDateKey(date);

    if (dateKey && dateKey !== lastDateKey) {
      items.push({
        type: 'divider',
        key: dateKey,
        label: formatDateDividerLabel(date)
      });
      lastDateKey = dateKey;
    }

    items.push({ type: 'message', message: msg });
  }

  return items;
}

function isForwardedMessage(msg) {
  if (!msg) return false;

  const text = String(msg.text || '').trim().toLowerCase();
  if (text === 'пересланное сообщение' || text === 'forward') {
    return true;
  }

  const attachments = normalizeAttachments(msg.attachments);
  if (!attachments.length) {
    return false;
  }

  return attachments.some((attachment) => {
    const type = String(attachment?.type || '').toLowerCase();
    if (type.includes('forward')) {
      return true;
    }

    return containsForwardMarker(attachment?.payload, 0);
  });
}

function normalizeAttachments(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function containsForwardMarker(value, depth) {
  if (!value || depth > 3) return false;

  if (typeof value === 'string') {
    return value.toLowerCase().includes('forward');
  }

  if (Array.isArray(value)) {
    return value.some((item) => containsForwardMarker(item, depth + 1));
  }

  if (typeof value === 'object') {
    return Object.entries(value).some(([k, v]) =>
      k.toLowerCase().includes('forward') || containsForwardMarker(v, depth + 1)
    );
  }

  return false;
}

function getDateKey(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDateDividerLabel(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const diffDays = Math.round((today - target) / 86400000);

  if (diffDays === 0) return 'Сегодня';
  if (diffDays === 1) return 'Вчера';

  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: now.getFullYear() === date.getFullYear() ? undefined : 'numeric'
  });
}

function getAvatarInitials(value) {
  const text = String(value || '').trim();
  if (!text) return 'U';

  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }

  const compact = parts[0];
  return compact.slice(0, 2).toUpperCase();
}

function loadRecentEmojis() {
  try {
    const raw = localStorage.getItem(RECENT_EMOJI_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((v) => typeof v === 'string' && !/^\?+$/.test(v.trim()))
      : [];
  } catch {
    return [];
  }
}

function saveRecentEmojis(list) {
  try {
    localStorage.setItem(RECENT_EMOJI_STORAGE_KEY, JSON.stringify(list));
  } catch {
    // ignore storage errors
  }
}

function extractAttachmentUrl(payload, preferImageExt) {
  if (!payload || typeof payload !== 'object') return '';

  const directKeys = [
    'url',
    'download_url',
    'preview_url',
    'full_url',
    'src',
    'image_url',
    'photo_url',
    'file_url',
    'video_url',
    'audio_url',
    'mp3',
    'ogg',
    'm4a',
    'aac',
    'opus',
    'wav'
  ];

  const find = (strictImage) => {
    for (const key of directKeys) {
      const value = payload?.[key];
      const found = pickUrlFromUnknown(value, strictImage);
      if (found) return found;
    }

    const nestedKeys = ['photos', 'images', 'files', 'video', 'audio', 'document', 'media', 'items', 'sizes', 'urls', 'streams', 'links'];
    for (const key of nestedKeys) {
      const value = payload?.[key];
      const found = pickUrlFromUnknown(value, strictImage);
      if (found) return found;
    }

    return pickUrlFromUnknown(payload, strictImage);
  };

  return find(preferImageExt) || (preferImageExt ? find(false) : '');
}

function pickUrlFromUnknown(value, preferImageExt) {
  if (!value) return '';

  if (typeof value === 'string') {
    return isHttpUrl(value) && (!preferImageExt || hasImageHint(value)) ? value : '';
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = pickUrlFromUnknown(item, preferImageExt);
      if (found) return found;
    }
    return '';
  }

  if (typeof value === 'object') {
    // Prefer explicit url-like fields first.
    const preferred = ['url', 'src', 'download_url', 'preview_url', 'full_url'];
    for (const key of preferred) {
      const found = pickUrlFromUnknown(value[key], preferImageExt);
      if (found) return found;
    }

    for (const key of Object.keys(value)) {
      const found = pickUrlFromUnknown(value[key], preferImageExt);
      if (found) return found;
    }
  }

  return '';
}

function isHttpUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value.trim());
}

function hasImageHint(value) {
  const v = String(value).toLowerCase();
  return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(v)
    || v.includes('image')
    || v.includes('/photo')
    || v.includes('/img');
}

function buildVideoStreamUrl(payload) {
  const tokenCandidate = findFirstVideoToken(payload);

  if (!tokenCandidate) {
    const directUrl = extractAttachmentUrl(payload, false);
    if (!directUrl) {
      return '';
    }
    return `/api/messages/video/proxy?url=${encodeURIComponent(directUrl)}`;
  }

  return `/api/messages/video/${encodeURIComponent(String(tokenCandidate))}/stream`;
}

function findFirstVideoToken(value) {
  return findFirstAttachmentToken(value, ['token', 'video_token', 'videotoken', 'attachment_token', 'upload_token']);
}

function buildAudioStreamUrl(payload) {
  const tokenCandidate = findFirstAudioToken(payload);

  if (!tokenCandidate) {
    const directUrl = extractAttachmentUrl(payload, false);
    if (!directUrl) {
      return '';
    }
    return `/api/messages/audio/proxy?url=${encodeURIComponent(directUrl)}`;
  }

  return `/api/messages/audio/${encodeURIComponent(String(tokenCandidate))}/stream`;
}

function findFirstAudioToken(value) {
  return findFirstAttachmentToken(value, ['token', 'audio_token', 'audiotoken', 'attachment_token', 'upload_token']);
}

function findFirstAttachmentToken(value, tokenKeys) {
  const walk = (node, parentKey = '') => {
    if (!node) return '';

    if (typeof node === 'string') {
      const trimmed = node.trim();
      if (!trimmed || /^https?:\/\//i.test(trimmed)) {
        return '';
      }

      // MAX docs: token comes from UploadedInfo/token-like fields.
      const key = String(parentKey || '').toLowerCase();
      const isTokenField = key.includes('token');
      if (!isTokenField) {
        return '';
      }

      return /^[A-Za-z0-9_-]{6,}$/.test(trimmed) ? trimmed : '';
    }

    if (Array.isArray(node)) {
      for (const item of node) {
        const token = walk(item, parentKey);
        if (token) return token;
      }
      return '';
    }

    if (typeof node !== 'object') return '';

    // Prioritize explicit token fields.
    for (const key of Object.keys(node)) {
      if (tokenKeys.includes(String(key).toLowerCase())) {
        const token = walk(node[key], key);
        if (token) return token;
      }
    }

    for (const key of Object.keys(node)) {
      const token = walk(node[key], key);
      if (token) return token;
    }

    return '';
  };

  return walk(value);
}

function isVideoLikePayload(payload) {
  if (!payload || typeof payload !== 'object') return false;

  const mimeType = String(payload?.mime_type || payload?.content_type || '').toLowerCase();
  if (mimeType.startsWith('video/')) return true;

  const fileName = String(payload?.file_name || payload?.filename || payload?.name || payload?.title || '').toLowerCase();
  if (/\.(mp4|webm|mov|mkv|avi|m4v)(\?|$)/i.test(fileName)) return true;

  return false;
}

function isAudioLikePayload(payload) {
  if (!payload || typeof payload !== 'object') return false;

  const mimeType = String(payload?.mime_type || payload?.content_type || '').toLowerCase();
  if (mimeType.startsWith('audio/')) return true;

  const fileName = String(payload?.file_name || payload?.filename || payload?.name || payload?.title || '').toLowerCase();
  if (/\.(mp3|ogg|oga|wav|m4a|aac|opus|flac)(\?|$)/i.test(fileName)) return true;

  return false;
}

function inferVideoMimeType(payload) {
  const name = String(payload?.file_name || payload?.filename || payload?.name || payload?.title || '').toLowerCase();
  if (name.endsWith('.webm')) return 'video/webm';
  if (name.endsWith('.mov')) return 'video/quicktime';
  if (name.endsWith('.mkv')) return 'video/x-matroska';
  if (name.endsWith('.avi')) return 'video/x-msvideo';
  if (name.endsWith('.m4v')) return 'video/x-m4v';
  return 'video/mp4';
}

function buildAudioSourceList(payload) {
  const list = [];
  const directUrl = extractAttachmentUrl(payload, false);
  const proxiedDirectUrl = directUrl
    ? `/api/messages/audio/proxy?url=${encodeURIComponent(directUrl)}`
    : '';
  const streamUrl = buildAudioStreamUrl(payload);

  // Never pass raw external media URL to browser to avoid mixed-content/cdn signature issues.
  // Prefer URL-proxy first (if present in payload), token-stream stays as fallback.
  if (proxiedDirectUrl) list.push(proxiedDirectUrl);
  if (streamUrl && streamUrl !== proxiedDirectUrl) list.push(streamUrl);

  return list;
}

function isAudioAttachmentType(type) {
  if (!type) return false;
  return type === 'audio'
    || type === 'voice'
    || type === 'ptt'
    || type.includes('audio')
    || type.includes('voice');
}

function isVideoAttachmentType(type) {
  if (!type) return false;
  return type === 'video' || type.includes('video');
}

function renderVideoAttachment(idx, payload) {
  const videoUrl = buildVideoStreamUrl(payload) || extractAttachmentUrl(payload, false);
  if (!videoUrl) {
    return <div key={idx} style={{ marginTop: '8px', color: '#666' }}>Видео (нет URL)</div>;
  }

  return (
    <div key={idx}>
      <video
        controls
        autoPlay={false}
        preload="metadata"
        style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '4px', marginTop: '8px' }}
      >
        <source src={videoUrl} type={payload?.mime_type || 'video/mp4'} />
        Ваш браузер не поддерживает видео
      </video>
    </div>
  );
}

function renderAudioAttachment(idx, payload) {
  const audioSources = buildAudioSourceList(payload);
  if (audioSources.length === 0) {
    return <div key={idx} style={{ marginTop: '8px', color: '#666' }}>Голосовое (нет URL)</div>;
  }

  const initialDuration = Number(payload?.duration ?? payload?.duration_seconds ?? 0);
  return (
    <VoiceMessagePlayer
      key={idx}
      sources={audioSources}
      initialDuration={Number.isFinite(initialDuration) ? initialDuration : 0}
    />
  );
}

function VoiceMessagePlayer({ sources, initialDuration = 0 }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [durationSec, setDurationSec] = useState(initialDuration > 0 ? initialDuration : 0);
  const [currentSec, setCurrentSec] = useState(0);
  const [sourceIndex, setSourceIndex] = useState(0);
  const activeSrc = sources[sourceIndex] || '';

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const onLoadedMetadata = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setDurationSec(Math.floor(audio.duration));
      }
    };
    const onTimeUpdate = () => {
      if (Number.isFinite(audio.currentTime) && audio.currentTime >= 0) {
        setCurrentSec(Math.floor(audio.currentTime));
      }
    };
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentSec(0);
      audio.currentTime = 0;
    };
    const onPause = () => setIsPlaying(false);
    const onPlay = () => setIsPlaying(true);
    const onError = () => {
      setIsPlaying(false);
      setCurrentSec(0);
      setSourceIndex((prev) => (prev + 1 < sources.length ? prev + 1 : prev));
    };

    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('error', onError);

    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('error', onError);
    };
  }, [sources.length]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !activeSrc) return;
    audio.load();
  }, [activeSrc]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      if (audio.paused) {
        await audio.play();
      } else {
        audio.pause();
      }
    } catch {
      // ignore play promise errors
    }
  };

  const shownSec = isPlaying ? currentSec : durationSec;

  return (
    <div className="voice-message">
      <audio ref={audioRef} preload="none" autoPlay={false}>
        <source src={activeSrc} />
      </audio>
      <button type="button" className="voice-play-btn" onClick={togglePlay} aria-label={isPlaying ? 'Пауза' : 'Воспроизвести'}>
        {isPlaying ? '⏸' : '▶'}
      </button>
      <div className="voice-meta">
        <div className="voice-wave" />
        <span className="voice-time">{formatVoiceTime(shownSec)}</span>
      </div>
    </div>
  );
}

function formatVoiceTime(totalSec) {
  const sec = Number.isFinite(totalSec) && totalSec > 0 ? Math.floor(totalSec) : 0;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function isMessageUnread(message, lastReadAt) {
  const readAt = Number(lastReadAt) || 0;
  if (readAt <= 0) return true;
  const messageDate = parseServerDate(message?.timestampMs ?? message?.timestamp);
  if (!(messageDate instanceof Date) || Number.isNaN(messageDate.getTime())) return false;
  return messageDate.getTime() > readAt;
}

function canDeleteMessageByAge(message) {
  const messageDate = parseServerDate(message?.timestampMs ?? message?.timestamp);
  if (!(messageDate instanceof Date) || Number.isNaN(messageDate.getTime())) {
    return true;
  }

  return (Date.now() - messageDate.getTime()) <= DELETE_MESSAGE_WINDOW_MS;
}

function getMessageReplyPreviewText(message) {
  const rawText = stripHtml(message?.text || '').trim();
  const cleanedText = rawText.replace(/^\s*["“”«»]\s*/, '');
  const text = cleanedText || 'Вложение';
  if (text.length <= 180) return text;
  return `${text.slice(0, 180)}...`;
}

function stripAuthorPrefix(text, authorName) {
  const source = String(text || '').trim();
  const author = String(authorName || '').trim();
  if (!source || !author) {
    return source;
  }

  const escapedAuthor = author.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const prefixRegex = new RegExp(`^\\s*${escapedAuthor}\\s*:\\s*(?:\\r?\\n\\s*){0,3}`, 'i');
  return source.replace(prefixRegex, '').trim();
}

export default ChatWindow;

